import React, { useState, useEffect, useContext } from 'react';
import { pipe } from 'fp-ts/lib/pipeable';
import { fold, Option, getOrElse } from 'fp-ts/lib/Option';
import NumberFormat, { NumberFormatValues } from 'react-number-format';
import { CalculatorStepsContext } from '../../Store/CalculatorStepsContext';
import { VALIDATION_NO_ALLOCATION_MAY_BE_ZERO, VALIDATION_ALLOCATIONS_MUST_SUM_TO_100 } from '../../../model/CalculatorInputsValidation';


interface Props {
    index: number;
    validationIssue: string;

    invalid: boolean;
    validationMessageId: string;
    showInput: boolean;
    allocation: Option<number>;
    setAllocation: (percentage: number) => void;
    unsetAllocation: () => void;
    onBlur: () => void;
}


export const AllocationPercentageInput = (props: Props) => {

    const initialAllocation = pipe(props.allocation,
        fold(()=>'',
        (num) => num.toString()))

    const [percentage, setPercentage] = useState<string>(initialAllocation);

    useEffect(() => {
        pipe(props.allocation,
            fold(() => setPercentage(''),
            (percentValue) => setPercentage(percentValue.toString())))
    }, [props.allocation])


    
    //const inputRef = useRef<HTMLInputElement>(null);
    let inputEl: HTMLInputElement | undefined = undefined;
    const { steps } = useContext(CalculatorStepsContext);

    function isFirstZeroField(): boolean {
        const n = steps.inputs.strategyList.getStrategyCount();
        let firstZeroIdx = -1;
        for (let i=0; i<n; i++) {
            const alloc = steps.inputs.strategyList.getAllocatedStrategy(i).allocation;
            const value = getOrElse(()=>-2)(alloc);
            if (value === 0) {
                firstZeroIdx = i;
                break;
            }
        }
        return (firstZeroIdx === props.index);
    }

    function isFirstOver100ElseLast(): boolean {
        const n = steps.inputs.strategyList.getStrategyCount();
        let firstZeroIdx = -1;
        let runningTotal = 0;
        for (let i=0; i<n; i++) {
            const alloc = steps.inputs.strategyList.getAllocatedStrategy(i).allocation;
            const value = getOrElse(()=>0)(alloc);
            runningTotal += value;
            if (runningTotal > 100) {
                firstZeroIdx = i;
                break;
            }
        }
        if (runningTotal > 100) {
            return (firstZeroIdx === props.index);
        } else if (runningTotal < 100) {
            // sum is less than 100%, grab focus on last field
            return (n-1 === props.index);
        } else {
            return false;
        }
    }

    useEffect(() => {
        const grabFocus = async () => {
            pipe(steps.firstValidationError,
                fold(()=> {return;},
                (firstError) => {
                    const issueWithThisField = (props.validationIssue === firstError)
                    || firstError === VALIDATION_NO_ALLOCATION_MAY_BE_ZERO && isFirstZeroField()
                    || firstError === VALIDATION_ALLOCATIONS_MUST_SUM_TO_100 && isFirstOver100ElseLast();
                    if (issueWithThisField) {
                        if (inputEl) {
                            inputEl.focus();
                        }
                    }
                }));
        };
        setTimeout(grabFocus, 25);
    }, [steps.firstValidationError])

    const allocationInputId = `rila-calculator-allocation-input-${props.index + 1}`;
    const allocationInput1Id = `rila-calculator-allocation-input-1`;

    const allocationLabel = (props.index === 0) ?
     <label id="rila-calculator-allocation-1-label" htmlFor={allocationInput1Id}><span className="sr-only">Strategy {props.index + 1} Percentage </span>Allocation</label> : 
     <label id={`rila-calculator-allocation-${props.index + 1}-label`} className="sr-only">Strategy {props.index + 1} Percentage Allocation</label>;
    const strategySelectorClasses = ["step-4-row", `strategy-row-${props.index + 1}`, "allocation-input", `allocation-input-${props.index + 1}`];

    const updateAmountEntered = (v: NumberFormatValues) => {        
        setPercentage(v.value);
        if (v.value === '') {
            props.unsetAllocation();
        } else {
            props.setAllocation(parseInt(v.value))
        }
    }

    
    const inputRowClasses = props.invalid
        ? "allocation-input-row validation-error"
        : "allocation-input-row";

    const describedBy = props.invalid ? props.validationMessageId : undefined;

    return (
        <div className={strategySelectorClasses.join(' ')}>

            {allocationLabel}
            <div className={inputRowClasses}>
                {props.showInput
                    ? <NumberFormat displayType="input"
                        id={allocationInputId}
                        suffix="%"
                        size={4}
                        maxLength={4}
                        className="form-control allocation-percentage"
                        value={percentage}
                        aria-required={true}
                        aria-invalid={props.invalid}
                        aria-labelledby={`rila-calculator-allocation-${props.index + 1}-label`}
                        aria-describedby={describedBy}
                        isNumericString={true}
                        isAllowed={(v) => v.value.match(/^\d*$/) !== null}
                        onValueChange={updateAmountEntered}
                        onBlur={props.onBlur}
                        getInputRef={(el: HTMLInputElement) => { inputEl = el; }}
                    />
                    : null}
            </div>
        </div>
    )
}

